// frontend/src/services/api.js
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8008';

export const login = async (password) => {
  try {
    console.log("Attempting login with password");
    
    // Attempt to validate the password with a dedicated endpoint
    const response = await fetch(`${API_URL}/auth/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });
    
    if (!response.ok) {
      console.error("Login failed with status:", response.status);
      throw new Error('Invalid password');
    }
    
    // Store password in cookie for future requests
    document.cookie = `shared_password=${encodeURIComponent(password)}; path=/; secure; samesite=strict`;
    return true;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const loginAsInstructor = async (credentials) => {
  try {
    console.log("Attempting instructor login for:", credentials.email);
    
    const response = await fetch(`${API_URL}/instructor/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });
    
    console.log("Login response status:", response.status);
    
    if (!response.ok) {
      const error = await response.json();
      console.error("Login error details:", error);
      throw new Error(error.detail || 'Invalid credentials');
    }
    
    const data = await response.json();
    console.log("Login successful, received token");
    
    // Store token in localStorage
    localStorage.setItem('instructor_token', data.token);
    localStorage.setItem('instructor_name', data.name);
    
    return data;
  } catch (error) {
    console.error('Instructor login error:', error);
    throw error;
  }
};

export const sendQuestion = async (question, tonePreference = 'casual') => {
  try {
    const response = await fetch(`${API_URL}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': getCookie('shared_password')
      },
      body: JSON.stringify({ question, tone_preference: tonePreference }),
      credentials: 'include',
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to get response');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error sending question:', error);
    throw error;
  }
};

export const sendFeedback = async (messageId, isHelpful, comment = null) => {
  try {
    const response = await fetch(`${API_URL}/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': getCookie('shared_password')
      },
      body: JSON.stringify({ 
        message_id: messageId, 
        is_helpful: isHelpful,
        comment
      }),
      credentials: 'include',
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to send feedback');
    }
  } catch (error) {
    console.error('Error sending feedback:', error);
    throw error;
  }
};

export const getQuestions = async (limit = 10, skip = 0, days = null, search = '') => {
  const token = localStorage.getItem('instructor_token');
  
  if (!token) {
    throw new Error('Not authenticated as instructor');
  }
  
  try {
    let url = `${API_URL}/instructor/questions?limit=${limit}&skip=${skip}`;
    if (days) {
      url += `&days=${days}`;
    }
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get questions');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching questions:', error);
    throw error;
  }
};

export const getAnalytics = async (days = 7) => {
  const token = localStorage.getItem('instructor_token');
  
  if (!token) {
    throw new Error('Not authenticated as instructor');
  }
  
  try {
    const response = await fetch(`${API_URL}/instructor/analytics?days=${days}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get analytics');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching analytics:', error);
    throw error;
  }
};

export const getCurriculumGaps = async (days = 30, limit = 20, search = '') => {
  const token = localStorage.getItem('instructor_token');
  
  if (!token) {
    throw new Error('Not authenticated as instructor');
  }
  
  try {
    let url = `${API_URL}/instructor/curriculum-gaps?days=${days}&limit=${limit}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get curriculum gaps');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching curriculum gaps:', error);
    throw error;
  }
};

// Helper function to get a cookie value
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop().split(';').shift();
    // URL-decode the cookie value
    return decodeURIComponent(cookieValue);
  }
  return null;
}